import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { SofaPanel, SofaButton } from '@sofology/sofologycomponents';

import './FinanceApplicationPage.scss';

import { generateDispatchToPropsMap, paymentHelper, GtmCheckoutSteps, logsHelper, errorToBasketErrorModel, scrollHelper } from 'helpers';
import { AppState } from 'store';
import { IfcApplicationData, IlpQuestionDescriptions, NotificationTypes } from 'models';
import { BasketDetailsModel } from 'models/Basket';
import { DEFAULT_ERROR_TEXT } from 'models/Errors';
import { routes } from 'routes';
import { Loader, BasketModal, ShortOrderSummary, StepBar } from 'components';
import FinancePersonalDetailsForm, { FinancePersonalDetailsFormType } from './FinancePersonalDetailsForm/FinancePersonalDetailsForm';
import { PaymentDetailsModel } from 'models/Payment';
import { addNotification, AddNotificationFunc } from 'store/notifications/actions';
import { initFirstFinanceApplicationPage, InitFirstFinanceApplicationPageFunc, SaveIfcFinanceDataFunc, saveIfcFinanceData } from 'store/checkout/actions/finance';
import { preprocessRedirectToNextStep, PreprocessRedirectToNextStepFunc } from 'store/checkout/actions';
import { setShowReservationWarning, SetShowReservationWarningFunc } from 'store/basket/actions';

interface OwnState {
    isContinueClickProcessing: boolean;
    isContinueButtonDisabled: boolean;
}

interface StateProps {
    basket: BasketDetailsModel;
    paymentDetails: PaymentDetailsModel;
    isBasketLoading: boolean;
    shouldShowReservationWarning: boolean;
    ifcQuestionSet: IlpQuestionDescriptions | null;
    ifcApplicationData: IfcApplicationData | null;
    firstFinanceApplicationPageInitialized: boolean;
}

interface DispatchProps {
    initFirstFinanceApplicationPage: InitFirstFinanceApplicationPageFunc;
    saveIfcFinanceData: SaveIfcFinanceDataFunc;
    addNotification: AddNotificationFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
    setShowReservationWarning: SetShowReservationWarningFunc;
}

const DEFAULT_APR_PERCENT = 0;
const CURRENT_STEP = 1;
const STEPS_COUNT = 4;

class FinanceApplicationPage extends Component<StateProps & DispatchProps & RouteComponentProps, OwnState> {
    private personalDetailsForm = React.createRef<FinancePersonalDetailsFormType>();

    constructor(props: StateProps & DispatchProps & RouteComponentProps) {
        super(props);

        this.state = {
            isContinueClickProcessing: false,
            isContinueButtonDisabled: false
        };
    }

    componentDidMount() {
        if (!this.props.firstFinanceApplicationPageInitialized) {
            this.props.initFirstFinanceApplicationPage();
        }
    }

    componentDidUpdate(prevProps: Readonly<StateProps & DispatchProps & RouteComponentProps>) {
        /** redirect if payment type was changed on the basket page */
        if (paymentHelper.isCardRelatedPaymentType(this.props.paymentDetails.paymentType)) {
            this.props.history.push(routes.payment.path);
        }

        if (!prevProps.basket && !this.props.basket && !prevProps.isBasketLoading && !this.props.isBasketLoading) {
            this.props.addNotification(DEFAULT_ERROR_TEXT, NotificationTypes.Err);
        }
    }

    handleContinueClick = () => {
        this.setState({ isContinueClickProcessing: true }, () => {
            this.validateForm()
                .then(isValid => {
                    if (isValid) {
                        const formComponent = this.personalDetailsForm.current!;
                        this.props.saveIfcFinanceData(formComponent.getFormData());

                        this.props.preprocessRedirectToNextStep(
                            () => this.props.history.push(routes.financeApplicationStepTwo.path),
                            GtmCheckoutSteps.FinanceFormStepTwo
                        );
                    } else {
                        this.setState({ isContinueClickProcessing: false });
                    }
                })
                .catch(error => {
                    logsHelper.error(errorToBasketErrorModel(error, 'FinanceApplicationPage:handleContinueClick'));
                    this.setState({ isContinueClickProcessing: false });
                });
        });
    };

    handleReservationWarningClick = () => {
        this.props.setShowReservationWarning(false);
        this.props.history.push(routes.basket.path);
    };

    handleGoToStartCheckout = () => {
        this.props.history.push(routes.deliveryDetails.path);
    };

    handleDynamicFormInitialized = () => {
        this.setState({ isContinueButtonDisabled: this.personalDetailsForm.current!.hasInvalidReadonlyControls() });
    };

    render(): JSX.Element {
        const { firstFinanceApplicationPageInitialized } = this.props;

        return (
            <div className='finance-application-page'>
                {
                    firstFinanceApplicationPageInitialized
                        ? this.renderContent()
                        : <Loader show={true} />
                }
            </div>
        );
    }

    renderContent(): JSX.Element {
        const { shouldShowReservationWarning } = this.props;

        return (
            <>
                {this.renderShortOrderSummaryPanel()}
                <StepBar
                    stepsCount={STEPS_COUNT}
                    currentStep={CURRENT_STEP}
                    title='Your finance application for interest Free (0%) credit' />
                <SofaPanel>
                    <FinancePersonalDetailsForm
                        ifcApplicationData={this.props.ifcApplicationData!}
                        ifcQuestionSet={this.props.ifcQuestionSet!}
                        ref={this.personalDetailsForm}
                        onGoToStartCheckout={this.handleGoToStartCheckout}
                        onDynamicFormInitialized={this.handleDynamicFormInitialized}
                    />
                </SofaPanel>
                {this.renderContinueButton()}
                <BasketModal open={shouldShowReservationWarning} onClick={this.handleReservationWarningClick} />
            </>
        );
    }

    renderShortOrderSummaryPanel(): JSX.Element {
        const { basket, paymentDetails } = this.props;

        return (
            <SofaPanel>
                <ShortOrderSummary
                    total={basket.totalPriceWithTax.value}
                    leadTime={basket.leadTime}
                    eca={basket.eca}
                    deposit={paymentDetails.ifcDeposit}
                    monthlyPayment={paymentDetails.ifcMonthlyAmount}
                    term={paymentDetails.ifcNumMonths}
                    percentage={DEFAULT_APR_PERCENT}
                    hasQuickstockItem={basket.hasQuickstockItem()}
                />
            </SofaPanel>
        );
    }

    renderContinueButton(): JSX.Element {
        return (
            <div className='finance-application-page__continue-button'>
                {this.state.isContinueClickProcessing && <Loader show={true} overlay={true} />}
                <SofaButton
                    text='Continue'
                    variant='primary'
                    disabled={this.state.isContinueButtonDisabled}
                    onClick={this.handleContinueClick}
                />
            </div>
        );
    }

    async validateForm(): Promise<boolean> {
        let isValid = true;
        const formComponent = this.personalDetailsForm.current!;
        const invalidControls = await formComponent.validate();

        if (invalidControls.length > 0) {
            isValid = false;
            let firstInvalidControl = invalidControls.min(c => c.index);
            firstInvalidControl.ref?.current && scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
        }

        return isValid;
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        basket: state.basketState.basket!,
        paymentDetails: state.paymentState.paymentDetails,
        isBasketLoading: state.basketState.isBasketLoading,
        shouldShowReservationWarning: state.basketState.shouldShowReservationWarning,
        ifcQuestionSet: state.checkoutState.ifcQuestionSet,
        ifcApplicationData: state.checkoutState.ifcApplicationData,
        firstFinanceApplicationPageInitialized: state.checkoutState.firstFinanceApplicationPageInitialized
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch,
        [
            initFirstFinanceApplicationPage,
            saveIfcFinanceData,
            addNotification,
            preprocessRedirectToNextStep,
            setShowReservationWarning
        ]);
}

export default connect(mapStateToProps, mapDispatchToProps)(FinanceApplicationPage);